import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getFiscalQuotaRepository } from "@/infrastructure/di";
import type {
  QuotaAggregateKey,
  QuotaExportParams,
  QuotaListFilters,
  QuotaSort,
} from "@/domain/fiscal/quotaList";
import type { ExportDownloadOptions } from "@/domain/export";
import { queryKeys } from "../queryKeys";
import { distinctQuotaPages } from "./quotaCache";

export const useQuotaList = (
  orgId: string,
  filters: QuotaListFilters,
  sort: QuotaSort[],
  pageSize: number,
) =>
  useInfiniteQuery({
    queryKey: queryKeys.beneficiaries.quotaList(orgId, { filters, sort }, { pageSize }),
    queryFn: ({ pageParam, signal }) =>
      getFiscalQuotaRepository().getQuotas(
        { ...filters, sort, limit: pageSize, offset: pageParam },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.quotas.length, 0);
      return loaded < lastPage.total_count ? loaded : undefined;
    },
    select: distinctQuotaPages,
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });

export const useQuotaAggregates = (
  orgId: string,
  filters: QuotaListFilters,
  aggregates: readonly QuotaAggregateKey[],
) =>
  useQuery({
    queryKey: queryKeys.beneficiaries.quotaAggregates(orgId, { ...filters }, aggregates),
    queryFn: ({ signal }) =>
      getFiscalQuotaRepository().getQuotaAggregates(
        { ...filters, aggregates: [...aggregates] },
        signal,
      ),
    enabled: !!orgId && aggregates.length > 0,
    placeholderData: keepPreviousData,
  });

export const useQuota = (orgId: string, quotaUuid: string | null) =>
  useQuery({
    queryKey: queryKeys.beneficiaries.quota(orgId, quotaUuid ?? ""),
    queryFn: ({ signal }) => getFiscalQuotaRepository().getQuota(quotaUuid!, signal),
    enabled: !!orgId && !!quotaUuid,
  });

export const downloadQuotaExport = (filters: QuotaExportParams, options: ExportDownloadOptions) =>
  getFiscalQuotaRepository().downloadExport(filters, options);
